/**
 * AccessControlOverview widget — headline counts for access control.
 *
 * 3 tiles:
 *   - Workers with login (workers that have a role_code)
 *   - Roles (total count, with custom vs built-in breakdown)
 *   - Pending Invites (workers with status='invited')
 *
 * Each tile clickable → navigates to the relevant page.
 */

import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { Person, Shield, MailOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, systemApi } from '../../../../api';
import { queryDashboard } from '../../../../api/dashboardQuery';
import { WidgetErrorState } from '../../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../../i18n/translateApiError';

interface Metric {
  label: string;
  count: number | null;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  path: string;
}

const AccessControlOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<Metric[]>([
    { label: t('dashboard:widgets.accessControl.workersWithLogin'), count: null, icon: <Person />, color: '#2196F3', path: '/organization/workers/active' },
    { label: t('dashboard:widgets.accessControl.roles'), count: null, icon: <Shield />, color: '#9C27B0', path: '/system/access-control/roles' },
    { label: t('dashboard:widgets.accessControl.pendingInvites'), count: null, icon: <MailOutline />, color: '#FF9800', path: '/organization/workers/active' },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const overview = await queryDashboard(
          'system-overview',
          () => systemApi.systemOverviewApi.get(),
        );
        if (cancelled) return;

        const withLogin = overview.workers_with_login ?? 0;
        const pendingInvites = overview.pending_invites ?? 0;
        const customRoles = overview.custom_roles ?? 0;
        const totalRoles = overview.total_roles ?? 0;

        setMetrics([
          { label: t('dashboard:widgets.accessControl.workersWithLogin'), count: withLogin, icon: <Person />, color: '#2196F3', path: '/organization/workers/active' },
          { label: t('dashboard:widgets.accessControl.roles'), count: totalRoles, subtitle: customRoles > 0 ? t('dashboard:widgets.accessControl.customRoles', { count: customRoles }) : undefined, icon: <Shield />, color: '#9C27B0', path: '/system/access-control/roles' },
          { label: t('dashboard:widgets.accessControl.pendingInvites'), count: pendingInvites, icon: <MailOutline />, color: pendingInvites > 0 ? '#FF9800' : '#4CAF50', path: '/organization/workers/active' },
        ]);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.accessControl.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  if (loading) return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} /></Box>;
  if (error) return <WidgetErrorState error={error} />;

  return (
    <Box sx={{ height: '100%' }}>
      <Grid container spacing={1}>
        {metrics.map((m) => (
          <Grid item xs={4} key={m.label}>
            <Paper
              elevation={0}
              onClick={() => navigate(m.path)}
              sx={{
                p: 1.5, height: '100%', borderRadius: 2, bgcolor: 'background.paper',
                cursor: 'pointer', border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 0.5 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: m.color }}>
                  {m.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: m.color, lineHeight: 1.2 }}>
                  {m.count === null ? '—' : m.count}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                  {m.label}
                </Typography>
                {m.subtitle && <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem', lineHeight: 1 }}>{m.subtitle}</Typography>}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AccessControlOverview;
